import {
  SkyButton,
  SkyCheckbox,
  SkyMessageBar,
  SkySwitch,
  SkyText,
  type CheckboxState,
} from "@skypointcloud/skypoint-ui";
import { useId, useState, type ReactElement } from "react";
import { useLocation } from "wouter";
import type { DbtJobView } from "@shared/types/transformations";
import { usePermissions } from "@/hooks/use-permissions";
import { ROUTES, absolutePath } from "@/lib/routes";
import { ASK_ADMIN_DBT_REPOSITORY } from "@/lib/transformations/dbtSettings";
import type { ScheduleDraft } from "./scheduleForm";

/**
 * The Transformations part of What runs: the dbt models a schedule runs (ported from Studio's
 * `ScheduleTransformationV2`).
 *
 * Round 6, S1 and S4 (owner, 2026-10-01: "What runs, should have two sections. 1. Connection
 * 2. Transformation. At least one is required."): this was the last card, "Then", while every
 * schedule imported first. A schedule can now run dbt jobs only, over what is already in
 * the warehouse, so the models are part of what runs and sit under the connections in one card.
 *
 * Three states, and the difference between the last two matters. `null` means
 * this deployment has no dbt jobs API — the section says so and offers
 * nothing. An empty array means the API answered and this instance has no
 * models yet. Collapsing them would make a route that is not mounted look
 * exactly like a correctly configured instance with nothing in it, which is the
 * harder of the two to notice.
 *
 * More than `COLLAPSE_AFTER` models make the card long, so the list folds to "3 of 12 models
 * chosen" with Change, once, while nothing on it has an error.
 */

/** Above this many models the list starts folded (S4). */
export const COLLAPSE_AFTER = 8;

export const TRANSFORMATIONS_HINT =
  "Their models run in dependency order. With no connection, they run over what is already in the warehouse.";

/** Round 7: the one freshness control's words. */
export const SOURCE_FRESHNESS_LABEL = "Skip when a source is stale";
export const SOURCE_FRESHNESS_HINT =
  "Before each scheduled run, dbt source freshness checks the sources these dbt jobs read. When one is past the freshness limit set in your dbt project, the run builds nothing and says why. Run now always builds.";

/**
 * Round 7 (backlog "Check freshness before a transform-only schedule runs"): the check is offered
 * for a schedule of dbt jobs and no connection, whose models read tables another system writes.
 * A schedule with a connection builds right after its own import landed.
 */
export function offersSourceFreshness(
  draft: Pick<ScheduleDraft, "dataflowIds" | "dbtJobIds">,
): boolean {
  return draft.dbtJobIds.length > 0 && draft.dataflowIds.length === 0;
}

export interface ScheduleDbtJobsSectionProps {
  draft: ScheduleDraft;
  onChange: (draft: ScheduleDraft) => void;
  transformations: DbtJobView[] | null | undefined;
  isLoading?: boolean;
  disabled?: boolean;
  /**
   * False when this instance has no dbt repository bound (round 6, W11): the empty state then
   * names where the repository is set, which is the step before a model can exist. Undefined
   * while unknown.
   */
  repoBound?: boolean;
  /**
   * Field issues the server answered with, keyed by the field it named. Only
   * `dbtJobIds` is shown here: a failed save that names it focuses What runs, so the
   * reason has to be in it (review P6 r2).
   */
  fieldErrors?: Record<string, string>;
}

/** The empty state: where the repository is set when none is bound, else where jobs are added. */
function NoDbtJobs({
  repoBound,
  onOpenDbtSource,
}: {
  repoBound: boolean | undefined;
  onOpenDbtSource: () => void;
}) {
  // #1106: the dbt repository tab is Instance admin and above. A Contributor editing a schedule
  // gets no button that would leave the draft for a screen they cannot open, and reads who acts.
  const { canAny } = usePermissions();
  if (repoBound === false && !canAny("instance.binding.read")) {
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="No dbt jobs in this instance yet"
        message={`No dbt repository is bound yet, so there is no dbt job to offer here. ${ASK_ADMIN_DBT_REPOSITORY}`}
        testId="schedule-transformations-empty"
      />
    );
  }
  if (repoBound === false) {
    return (
      <SkyMessageBar
        intent="info"
        layout="multiline"
        title="No dbt jobs in this instance yet"
        message="Bind a dbt repository first, under Settings > Destination > dbt repository. Then add a dbt job on the Transformations screen and it will be offered here."
        testId="schedule-transformations-empty"
        actions={
          <SkyButton
            appearance="secondary"
            size="small"
            testId="schedule-transformations-open-dbt-source"
            onClick={onOpenDbtSource}
          >
            Open dbt repository
          </SkyButton>
        }
      />
    );
  }
  return (
    <SkyMessageBar
      intent="info"
      layout="multiline"
      title="No dbt jobs in this instance yet"
      message="Add a dbt job on the Transformations screen and it will be offered here."
      testId="schedule-transformations-empty"
    />
  );
}

/** The folded "3 of 12 dbt jobs chosen" line with Change, or the list of dbt jobs to choose. */
function DbtJobsPicker({
  models,
  chosen,
  folded,
  chosenCount,
  disabled,
  onExpand,
  onSelect,
}: {
  models: DbtJobView[];
  chosen: string[];
  folded: boolean;
  chosenCount: number;
  disabled: boolean;
  onExpand: () => void;
  onSelect: (states: CheckboxState[] | boolean) => void;
}) {
  if (folded) {
    return (
      <div
        className="flex flex-wrap items-center gap-3"
        data-testid="schedule-transformations-folded"
      >
        <SkyText size={300} block>
          {`${chosenCount} of ${models.length} dbt jobs chosen`}
        </SkyText>
        <SkyButton
          appearance="secondary"
          size="small"
          onClick={onExpand}
          aria-label={`Change the dbt jobs, ${chosenCount} of ${models.length} chosen`}
          testId="schedule-transformations-change"
        >
          Change
        </SkyButton>
      </div>
    );
  }
  return (
    <SkyCheckbox
      label="dbt jobs this schedule runs"
      layout="vertical"
      selectAll="Every dbt job"
      options={models.map((transformation) => ({
        id: transformation.id,
        label: transformation.name,
        description: transformation.enabled
          ? (transformation.dbtSelector ?? "Runs after the load step")
          : "Disabled. It will be skipped even when this schedule names it",
        disabled: !transformation.enabled,
      }))}
      value={chosen}
      onChange={onSelect}
      disabled={disabled}
      testId="schedule-transformations"
    />
  );
}

export function ScheduleDbtJobsSection({
  draft,
  onChange,
  transformations,
  isLoading = false,
  disabled = false,
  repoBound,
  fieldErrors = {},
}: ScheduleDbtJobsSectionProps) {
  const headingId = useId();
  const [, navigate] = useLocation();
  const [expanded, setExpanded] = useState(false);

  const setSelected = (states: CheckboxState[] | boolean): void => {
    if (typeof states === "boolean") return;
    onChange({
      ...draft,
      dbtJobIds: states.filter((state) => state.checked).map((state) => state.id),
    });
  };

  const models = transformations ?? [];
  const chosenCount = models.filter((model) => draft.dbtJobIds.includes(model.id)).length;
  const folded = !expanded && models.length > COLLAPSE_AFTER && !fieldErrors.dbtJobIds;
  // The hint and the freshness switch follow a list that has loaded and holds a job.
  const listed = models.length > 0 && !isLoading;

  /** The list: loading, unavailable in this deployment, no dbt job yet, or the picker. */
  const renderJobs = (): ReactElement => {
    if (isLoading) {
      return (
        <SkyText size={300} color="muted">
          Loading the models in this instance…
        </SkyText>
      );
    }
    if (transformations === null) {
      return (
        <SkyMessageBar
          intent="info"
          layout="multiline"
          title="Transformations are not available in this deployment"
          message="This schedule will import its connections and stop there. Nothing is lost: dbt jobs can be added to it later."
          testId="schedule-transformations-unavailable"
        />
      );
    }
    if (models.length === 0) {
      return (
        <NoDbtJobs
          repoBound={repoBound}
          onOpenDbtSource={() => navigate(absolutePath(`${ROUTES.settingsDestination}?tab=dbt`))}
        />
      );
    }
    return (
      <DbtJobsPicker
        models={models}
        chosen={draft.dbtJobIds}
        folded={folded}
        chosenCount={chosenCount}
        disabled={disabled}
        onExpand={() => setExpanded(true)}
        onSelect={setSelected}
      />
    );
  };

  return (
    <section
      aria-labelledby={headingId}
      data-testid="schedule-transformations-section"
      className="flex flex-col gap-3"
    >
      <SkyText as="h3" id={headingId} size={300} weight="semibold">
        dbt jobs
      </SkyText>
      {renderJobs()}
      {listed ? (
        <SkyText size={200} color="muted" block testId="schedule-transformations-hint">
          {TRANSFORMATIONS_HINT}
        </SkyText>
      ) : null}

      {listed && offersSourceFreshness(draft) ? (
        <div className="flex flex-col gap-1" data-testid="schedule-source-freshness">
          <SkySwitch
            label={SOURCE_FRESHNESS_LABEL}
            checked={draft.checkSourceFreshness}
            onCheckedChange={(next) => onChange({ ...draft, checkSourceFreshness: next })}
            disabled={disabled}
            testId="schedule-source-freshness-switch"
          />
          <SkyText size={200} color="muted" block>
            {SOURCE_FRESHNESS_HINT}
          </SkyText>
        </div>
      ) : null}

      {fieldErrors.dbtJobIds ? (
        <SkyMessageBar
          intent="error"
          layout="multiline"
          title="This schedule names a dbt job that cannot run"
          message={fieldErrors.dbtJobIds}
          testId="schedule-transformations-error"
        />
      ) : null}
    </section>
  );
}
